import dayjs from 'dayjs';
import React from 'react';
import { UiVaultConfig } from 'src/@types/vaults';
import { getUrlForAccount } from 'src/utils/explorer';
import { cj } from 'src/utils/classNames';
import useIsMobileScreenSize from '../../../../hooks/useIsMobileScreenSize';
import { usePlaceholdersReplacements } from 'src/hooks/usePlaceholdersReplacements';
import VaultOverviewContactsBox from './v2Components/VaultOverviewContactsBox';
import VaultOverviewMarkdownContent from './v2Components/VaultOverviewMarkdownContent';
import { VaultOverviewSectionBox } from './VaultOverviewSectionBox';
import { Section } from './v1Components';
import { ArrowRight } from '@drift-labs/icons';

/*
 * NOTE ON VAULT OVERVIEW COMPONENTS
 *
 * Version 1:
 * In the v1 version of the overview page we needed to construct the content for the page piece-by-piece in the vaults dashboard. The v1 stuff should essentially be deprecated and only maintained for any vaults which haven't been updated to provide their content in the v2 format yet.
 *
 * Version 2:
 * In v2 this was updated to accept single large blocks of markdown content and directly render them in the UI. This also included a design change where we included a seperate "side panel" for extra content, and a contacts box.
 *
 * - When adding the vault in the dashboard, it needs to be decided which type of display to use - becaause both can't be displayed at the same time.
 * - The components have (mostly) been split into seperate folders to help reduce confusion between the two branches of components.
 */

const FeeChangeSegment = ({
	feeChange,
}: {
	feeChange: UiVaultConfig['feeChanges'][number];
}) => {
	return (
		<>
			<span className="typo-t4 text-text-secondary">
				{feeChange.type === 'managerFee'
					? 'Management Fee'
					: 'Performance Fee'}{' '}
			</span>
			<span className="inline-flex items-center gap-1 typo-t4 text-text-secondary">
				{feeChange.oldValue}% <ArrowRight />
				{feeChange.newValue}%{' '}
				{`(${
					feeChange.newValue > feeChange.oldValue ? 'increase' : 'decrease'
				})`}
			</span>
			<span className="typo-t4 text-text-secondary">
				{dayjs(feeChange.changeDateTs).format('MMM D, YYYY')}
			</span>
		</>
	);
};

const VaultOverviewMainContent = (props: { uiVaultConfig: UiVaultConfig }) => {
	const placeholdersReplacements = usePlaceholdersReplacements();

	return (
		<>
			{props.uiVaultConfig.overviewSection?.map((section) => (
				<React.Fragment key={section.title}>
					<VaultOverviewSectionBox>
						<Section
							key={section.title}
							section={section}
							placeholdersReplacements={placeholdersReplacements}
						/>
					</VaultOverviewSectionBox>
				</React.Fragment>
			))}
		</>
	);
};

export default function VaultOverview(props: { uiVaultConfig: UiVaultConfig }) {
	const placeholdersReplacements = usePlaceholdersReplacements();

	const feeChanges = props.uiVaultConfig.feeChanges;

	const shouldUseMarkdownContent = props.uiVaultConfig.useMarkdownContent;

	const isMobile = useIsMobileScreenSize();

	return (
		<div className={cj('flex w-full flex-col gap-4', isMobile && 'px-2')}>
			<VaultOverviewSectionBox>
				<Section
					section={{
						title: 'Vault Address',
						paragraphs: [
							{
								text: props.uiVaultConfig.vaultPubkeyString,
							},
							{
								text: 'View on Drift',
								link: `https://app.drift.trade?authority=${props.uiVaultConfig.vaultPubkeyString}`,
							},
							{
								text: 'View on explorer',
								link: `${getUrlForAccount(
									props.uiVaultConfig.vaultPubkeyString
								)}`,
							},
						],
					}}
					placeholdersReplacements={placeholdersReplacements}
				/>
			</VaultOverviewSectionBox>

			{feeChanges?.length > 0 && (
				<VaultOverviewSectionBox>
					<span className="typo-t3">Historical Fee Changes</span>
					<div className="grid w-full grid-cols-3 gap-4">
						{feeChanges.map((feeChange) => (
							<FeeChangeSegment
								key={feeChange.type + feeChange.changeDateTs}
								feeChange={feeChange}
							/>
						))}
					</div>
					<span className="typo-t4 text-text-secondary">
						Any earnings prior to a fee change are unaffected by the change.
					</span>
				</VaultOverviewSectionBox>
			)}

			{shouldUseMarkdownContent ? (
				<>
					{isMobile && (
						<>
							<VaultOverviewMarkdownContent
								markdownContent={
									props.uiVaultConfig.vaultSidePanelMarkdownContent ?? ''
								}
							/>
							<VaultOverviewContactsBox
								contacts={props.uiVaultConfig.vaultSidePanelContactInformation}
							/>
						</>
					)}
					<VaultOverviewMarkdownContent
						markdownContent={
							props.uiVaultConfig.vaultMainPanelMarkdownContent ?? ''
						}
					/>
				</>
			) : (
				<VaultOverviewMainContent uiVaultConfig={props.uiVaultConfig} />
			)}
		</div>
	);
}
